/**
 * Single source of truth for turning a DSNY maintenance schedule into the
 * database artifacts a contract needs:
 *
 *   schedule rows (user input)
 *        │  deriveDsnyContractArtifacts()
 *        ├─ Building Upkeep package (billing)
 *        ├─ $0 child services per weekly collection type
 *        └─ standalone services for non-weekly custom/hallway rows
 *
 * Visit groups are derived separately (combination rules hook) but persisted
 * here so Create and Edit save them identically:
 *
 *   persistContractVisitGroups()   – insert groups + service mappings
 *   replaceContractVisitGroups()   – delete existing then insert (edit/save)
 *
 * Both persist functions first try the `replace_contract_visit_groups` RPC
 * (one transaction — all or nothing) and fall back to sequential inserts when
 * the RPC is not deployed yet.
 */

import { supabase } from './supabase'
import {
  ContractServiceForLink,
  DSNY_PACKAGE_SERVICE_ID,
  detectDsnyPackageKind,
  resolveDsnyCombineTarget,
  type DsnyCombineTarget,
  type DsnyPackageKind,
  resolveContractServiceIdForSave,
} from './contractServiceLineItems'
import {
  buildRowRecurrenceFields,
  getRowRecurrenceType,
  isPiggybackCombineRow,
  isRowWeekly,
  isSnapToClosestRow,
  type DsnyMonthlyMode,
  type DsnyRowRecurrence,
  type DsnyRowRecurrenceType,
} from './dsnyRowRecurrence'
import { suggestVisitGroupsDayCentric, type ServiceForGrouping } from './visitGroupSuggestion'
import {
  getGroupAssignments,
  getUnassignedServiceIds,
  syncGroupServiceIds,
} from './visitGroupBoardOps'
import type { ContractVisitGroup } from '../components/VisitGroupEditor'

export interface DsnyScheduleRowFull extends DsnyRowRecurrence {
  id: string
  name: string
  days: number[]
  combine_target?: DsnyCombineTarget
  assigned_crew_id?: string | null
  assigned_employee_id?: string | null
  /** Catalogue service item this row is linked to (required for custom rows). */
  service_item_id?: string
  /** Set when a row was rehydrated from a persisted contract_services row (edit). */
  persistedServiceId?: string
}

export type VisitGroupServiceMapping = {
  contract_service_id: string
  schedule_mode?: string
  recurrence_type?: string | null
  recurrence_weekday?: number | null
  recurrence_occurrence?: number | null
  recurrence_day_of_month?: number | null
  recurrence_nearest_visit?: boolean | null
  /** Host visit group metadata — needed to restore hallway vs DSNY piggyback targets. */
  visit_group_name?: string | null
  visit_group_dsny_integration?: boolean | null
}

/** Prefer explicitly persisted schedule.combine_target / column over host-group inference. */
function combineTargetFromSchedule(service: any): DsnyCombineTarget | undefined {
  const raw =
    service?.schedule?.combine_target ??
    service?.combine_target
  if (raw === 'dsny' || raw === 'hallway' || raw === null) return raw
  return undefined
}

function combineTargetFromMapping(
  mapping: VisitGroupServiceMapping | undefined,
  service: { dsny_integration?: boolean; schedule?: { dsny_integration?: boolean; combine_target?: DsnyCombineTarget } }
): DsnyCombineTarget {
  const fromSchedule = combineTargetFromSchedule(service)
  if (fromSchedule !== undefined) return fromSchedule

  if (mapping) {
    if (mapping.visit_group_dsny_integration) return 'dsny'
    if (/^Hallway Cleaning\b/i.test(mapping.visit_group_name || '')) return 'hallway'
    // Own / standalone visit group → separate visit.
    return null
  }
  if (service.dsny_integration || service.schedule?.dsny_integration) return 'dsny'
  return null
}

function schedField(service: any, field: string): number[] {
  const top = service?.[field]
  if (Array.isArray(top) && top.length > 0) return top
  const nested = service?.schedule?.[field]
  return Array.isArray(nested) ? nested : []
}

function isDsnyPackageService(service: any): boolean {
  if (service?.id === DSNY_PACKAGE_SERVICE_ID) return true
  const name = String(service?.service_name || '').trim().toLowerCase()
  if (name === 'building upkeep' || name.includes('exterior maintenance')) return true
  // Package billing row only — $0 children have dsny_integration but amount 0
  return !!(
    (service?.dsny_integration || service?.schedule?.dsny_integration) &&
    Number(service?.amount) > 0
  )
}

function packageKindFromServices(services: any[]): DsnyPackageKind {
  const pkg = services.find((s) => isDsnyPackageService(s))
  return detectDsnyPackageKind(pkg)
}

/** True when a $0 child is Garbage/Recycling/Organics/Bulk (not a custom schedule line). */
function isFixedWeeklyPickupChild(service: any): boolean {
  if (schedField(service, 'garbage_schedule').length > 0) return true
  if (schedField(service, 'recycling_schedule').length > 0) return true
  if (schedField(service, 'organics_schedule').length > 0) return true
  if (schedField(service, 'bulk_schedule').length > 0) return true
  return false
}

/** Map a $0 weekly child contract_service onto its synthetic schedule-row type. */
function weeklyPickupChildRowType(
  service: any
): 'garbage' | 'recycling' | 'organics' | 'bulk' | null {
  if (schedField(service, 'garbage_schedule').length > 0) return 'garbage'
  if (schedField(service, 'recycling_schedule').length > 0) return 'recycling'
  if (schedField(service, 'organics_schedule').length > 0) return 'organics'
  if (schedField(service, 'bulk_schedule').length > 0) return 'bulk'
  const name = String(service?.service_name || '')
    .trim()
    .toLowerCase()
  if (name === 'garbage') return 'garbage'
  if (name === 'recycling') return 'recycling'
  if (name === 'organics') return 'organics'
  if (name === 'bulk') return 'bulk'
  return null
}

function attachPersistedChildToRow(
  row: DsnyScheduleRowFull,
  service: any
): DsnyScheduleRowFull {
  const scheduleField =
    row.type === 'hallway_cleaning'
      ? 'interior_cleaning_schedule'
      : row.type === 'garbage'
        ? 'garbage_schedule'
        : row.type === 'recycling'
          ? 'recycling_schedule'
          : row.type === 'organics'
            ? 'organics_schedule'
            : row.type === 'bulk'
              ? 'bulk_schedule'
              : null
  const fromScheduleField = scheduleField ? schedField(service, scheduleField) : []
  const childDays =
    (Array.isArray(service.recurrence_days) && service.recurrence_days.length > 0
      ? service.recurrence_days
      : null) ||
    (Array.isArray(service.schedule?.recurrence_days) && service.schedule.recurrence_days.length > 0
      ? service.schedule.recurrence_days
      : null) ||
    fromScheduleField
  return {
    ...row,
    persistedServiceId: service.id,
    service_item_id: service.service_item_id || row.service_item_id,
    days: row.days?.length ? row.days : childDays,
  }
}

function customRowHasIdentity(row: { name?: string; service_item_id?: string }): boolean {
  return !!(row.service_item_id || (row.name || '').trim())
}

function resolveRowServiceItemId(
  row: DsnyScheduleRowFull,
  childItemsByType?: Partial<Record<string, { id: string; name: string } | null | undefined>>
): string {
  if (row.type === 'custom') return row.service_item_id || ''
  return childItemsByType?.[row.type]?.id || row.service_item_id || ''
}

function resolveRowServiceName(
  row: DsnyScheduleRowFull,
  childItemsByType?: Partial<Record<string, { id: string; name: string } | null | undefined>>
): string {
  if (row.type === 'custom') {
    return (row.name || '').trim() || 'Custom Schedule'
  }
  return (
    childItemsByType?.[row.type]?.name ||
    row.name ||
    (row.type === 'hallway_cleaning' ? 'Hallway Cleaning' : row.type)
  )
}

/**
 * Rebuild DSNY schedule rows from persisted contract_services (+ optional visit-group
 * mappings for monthly piggyback fields that live on the mapping, not the service).
 */
export function hydrateDsnyScheduleRowsFromContractServices(
  services: any[],
  mappings: VisitGroupServiceMapping[] = [],
  options?: { packageKind?: DsnyPackageKind }
): DsnyScheduleRowFull[] {
  const mappingByServiceId = new Map<string, VisitGroupServiceMapping>()
  for (const m of mappings) {
    mappingByServiceId.set(m.contract_service_id, m)
  }

  const hasSched = (field: string) => services.find((s) => schedField(s, field).length > 0)
  const packageKind = options?.packageKind ?? packageKindFromServices(services)
  const includeHallwayRow = packageKind !== 'exterior_maintenance'

  const rows: DsnyScheduleRowFull[] = []
  if (includeHallwayRow) {
    rows.push({
      id: 'hallway_cleaning',
      type: 'hallway_cleaning',
      name: 'Hallway Cleaning',
      days: schedField(hasSched('interior_cleaning_schedule'), 'interior_cleaning_schedule'),
      combine_target: 'hallway',
      combine_with_dsny: false,
    })
  }
  rows.push(
    {
      id: 'garbage',
      type: 'garbage',
      name: 'Garbage',
      days: schedField(hasSched('garbage_schedule'), 'garbage_schedule'),
      combine_target: 'dsny',
      combine_with_dsny: true,
    },
    {
      id: 'recycling',
      type: 'recycling',
      name: 'Recycling',
      days: schedField(hasSched('recycling_schedule'), 'recycling_schedule'),
      combine_target: 'dsny',
      combine_with_dsny: true,
    },
    {
      id: 'organics',
      type: 'organics',
      name: 'Organics',
      days: schedField(hasSched('organics_schedule'), 'organics_schedule'),
      combine_target: 'dsny',
      combine_with_dsny: true,
    },
    {
      id: 'bulk',
      type: 'bulk',
      name: 'Bulk',
      days: schedField(hasSched('bulk_schedule'), 'bulk_schedule'),
      combine_target: 'dsny',
      combine_with_dsny: true,
    }
  )

  for (const service of services) {
    if (isDsnyPackageService(service)) continue

    // Fixed weekly pickup children are represented via package schedule fields, but
    // board chips / visit-group mappings use the child's persisted uuid — attach it.
    const pickupType = weeklyPickupChildRowType(service)
    if (pickupType) {
      const idx = rows.findIndex((r) => r.type === pickupType)
      if (idx >= 0) rows[idx] = attachPersistedChildToRow(rows[idx], service)
      continue
    }

    const rtype = (service.recurrence_type ||
      service.schedule?.recurrence_type ||
      'weekly') as DsnyRowRecurrenceType
    const name = (service.service_name || '').trim()
    const isHallway = /^hallway cleaning$/i.test(name)

    // Weekly hallway child is represented via package interior_cleaning_schedule,
    // but visit-group mappings reference the child's uuid — attach it.
    if (isHallway && rtype === 'weekly') {
      if (!includeHallwayRow) continue
      const idx = rows.findIndex((r) => r.type === 'hallway_cleaning')
      if (idx >= 0) rows[idx] = attachPersistedChildToRow(rows[idx], service)
      continue
    }

    const mapping = mappingByServiceId.get(service.id)

    let monthly_mode: DsnyMonthlyMode = 'nth_weekday'
    const dayOfMonth =
      mapping?.recurrence_day_of_month ??
      service.recurrence_day_of_month ??
      service.schedule?.recurrence_day_of_month ??
      null
    if (dayOfMonth != null) monthly_mode = 'day_of_month'

    const combine_target = combineTargetFromMapping(mapping, service)
    const rowFields: Partial<DsnyScheduleRowFull> = {
      recurrence_type: rtype,
      monthly_mode,
      recurrence_weekday:
        mapping?.recurrence_weekday ??
        service.recurrence_weekday ??
        service.schedule?.recurrence_weekday ??
        null,
      recurrence_occurrence:
        mapping?.recurrence_occurrence ??
        service.recurrence_occurrence ??
        service.schedule?.recurrence_occurrence ??
        null,
      recurrence_day_of_month: dayOfMonth,
      combine_with_dsny: combine_target === 'dsny',
      combine_target,
      snap_to_closest_visit: !!mapping?.recurrence_nearest_visit,
      persistedServiceId: service.id,
      service_item_id: service.service_item_id || undefined,
      days:
        schedField(service, 'interior_cleaning_schedule').length > 0
          ? schedField(service, 'interior_cleaning_schedule')
          : service.recurrence_days || service.schedule?.recurrence_days || [],
    }

    if (isHallway) {
      // Exterior Maintenance contracts omit hallway; ignore leftover hallway services.
      if (!includeHallwayRow) continue
      const idx = rows.findIndex((r) => r.type === 'hallway_cleaning')
      if (idx >= 0) {
        rows[idx] = { ...rows[idx], ...rowFields }
      } else {
        rows.push({
          id: 'hallway_cleaning',
          type: 'hallway_cleaning',
          name: 'Hallway Cleaning',
          days: [],
          ...rowFields,
        })
      }
    } else {
      rows.push({
        id: service.id,
        type: 'custom',
        name: name || 'Custom Schedule',
        days: rowFields.days || [],
        ...rowFields,
      })
    }
  }

  return rows
}

/** Board / visit-group service entry for DSNY maintenance contracts. */
export type DsnyVisitBoardService = ServiceForGrouping

const COMBINED_WEEKLY_ROW_TYPES = new Set(['garbage', 'recycling', 'organics', 'bulk'])

/** Persisted package uuid on edit, or `dsny_package` sentinel on create. */
export function findDsnyPackageBoardId(contractServices: any[]): string {
  const pkg = contractServices.find((s) => {
    if (s.id === DSNY_PACKAGE_SERVICE_ID) return true
    const name = String(s.service_name || '')
      .trim()
      .toLowerCase()
    if (name === 'building upkeep' || name.includes('exterior maintenance')) return true
    return !!(s.dsny_integration && Number(s.amount) > 0)
  })
  return pkg?.id || DSNY_PACKAGE_SERVICE_ID
}

function rowQualifiesForBoard(row: DsnyScheduleRowFull): boolean {
  if (row.type === 'custom' && !customRowHasIdentity(row)) return false
  const rtype = getRowRecurrenceType(row)
  if (rtype === 'daily' || rtype === 'monthly') return true
  return (row.days || []).length > 0
}

/**
 * Services shown on the Crew Visits board — one chip per schedule row (Garbage,
 * Recycling, Hallway Cleaning, etc.). Building Upkeep is the merged billing /
 * contract_service on save only; it is not shown as a visit-board chip.
 */
export function buildDsnyVisitBoardServices(
  rows: DsnyScheduleRowFull[],
  _contractServices: any[] = [],
  _packageLabel?: string
): DsnyVisitBoardService[] {
  const out: DsnyVisitBoardService[] = []
  for (const row of rows) {
    if (!rowQualifiesForBoard(row)) continue
    const rec = buildRowRecurrenceFields(row)
    const combineTarget = resolveDsnyCombineTarget(row)
    // Custom rows must choose a visit target before appearing on the board.
    if (combineTarget === undefined) continue
    out.push({
      id: row.persistedServiceId || `dsny_${row.id}`,
      service_name: row.name || row.type,
      name: row.name || row.type,
      service_item_id: row.service_item_id,
      recurrence_days: row.days,
      dsny_integration: combineTarget === 'dsny',
      combine_with_dsny: combineTarget === 'dsny',
      combine_target: combineTarget,
      assigned_crew_id: row.assigned_crew_id || null,
      assigned_employee_id: row.assigned_employee_id || null,
      recurrence_type: rec.recurrence_type,
      recurrence_weekday: rec.recurrence_weekday,
      recurrence_occurrence: rec.recurrence_occurrence,
      recurrence_day_of_month: rec.recurrence_day_of_month,
      snap_to_closest: isSnapToClosestRow(row),
    })
  }
  return out
}

/** @deprecated Board and seed use the same per-row services now. */
export function expandBoardServicesForVisitGroupSeed(
  boardServices: DsnyVisitBoardService[],
  _rows: DsnyScheduleRowFull[]
): ServiceForGrouping[] {
  return boardServices
}

function isPackageAssignmentId(
  serviceId: string,
  packageBoardId: string,
  contractServices: any[]
): boolean {
  if (serviceId === packageBoardId || serviceId === DSNY_PACKAGE_SERVICE_ID) return true
  // Only the billing package (Building Upkeep / Exterior Maintenance) — not $0
  // weekly children that also carry dsny_integration=true.
  return contractServices.some((s) => s.id === serviceId && isDsnyPackageService(s))
}

/** Re-expand a persisted Building Upkeep assignment into Garbage / Recycling / … chips. */
export function expandPackageAssignmentsToScheduleRows(
  groups: ContractVisitGroup[],
  rows: DsnyScheduleRowFull[],
  packageBoardId: string,
  contractServices: any[] = []
): ContractVisitGroup[] {
  return groups.map((group) => {
    const assignments = getGroupAssignments(group)
    const hasPackagePrimary = assignments.some(
      (a) =>
        isPackageAssignmentId(a.service_id, packageBoardId, contractServices) &&
        a.schedule_mode !== 'piggyback'
    )
    if (!hasPackagePrimary) {
      return syncGroupServiceIds({ ...group, service_assignments: assignments })
    }

    const groupDays = new Set(group.recurrence_days || [])
    const isDsnyGroup = !!group.dsny_integration
    const rowPrimaries: Array<{ service_id: string; schedule_mode: 'primary' }> = []
    for (const row of rows) {
      if (!rowQualifiesForBoard(row) || !isRowWeekly(row)) continue
      if (!(row.days || []).some((d) => groupDays.has(d))) continue

      const combineTarget = resolveDsnyCombineTarget(row)

      if (isDsnyGroup) {
        if (combineTarget !== 'dsny') continue
      } else {
        const isHallwayGroup = /^Hallway Cleaning\b/i.test(group.name || '')
        if (combineTarget === 'dsny') continue
        if (combineTarget === 'hallway' && !isHallwayGroup) continue
        if (combineTarget === null && isHallwayGroup) continue
      }

      rowPrimaries.push({
        service_id: row.persistedServiceId || `dsny_${row.id}`,
        schedule_mode: 'primary',
      })
    }

    const others = assignments.filter(
      (a) => !isPackageAssignmentId(a.service_id, packageBoardId, contractServices)
    )
    const seen = new Set<string>()
    const merged = [...rowPrimaries, ...others].filter((a) => {
      if (seen.has(a.service_id)) return false
      seen.add(a.service_id)
      return true
    })
    return syncGroupServiceIds({ ...group, service_assignments: merged })
  })
}

const DAY_LABELS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function visitGroupAssignmentSignature(group: ContractVisitGroup): string {
  return JSON.stringify(
    getGroupAssignments(group)
      .map((a) => ({
        service_id: a.service_id,
        schedule_mode: a.schedule_mode || 'primary',
        recurrence_type: a.recurrence_type ?? null,
        recurrence_day_of_month: a.recurrence_day_of_month ?? null,
        recurrence_nearest_visit: a.recurrence_nearest_visit ?? null,
        recurrence_weekday: a.recurrence_weekday ?? null,
        recurrence_occurrence: a.recurrence_occurrence ?? null,
      }))
      .sort((a, b) => a.service_id.localeCompare(b.service_id))
  )
}

/** Merge DSNY day-slot groups that share the same service assignments (e.g. Wed + Fri). */
export function consolidateDsnyPackageVisitGroups(
  groups: ContractVisitGroup[],
  _packageBoardId: string
): ContractVisitGroup[] {
  const dsnyGroups: ContractVisitGroup[] = []
  const other: ContractVisitGroup[] = []

  for (const g of groups) {
    if (g.dsny_integration) dsnyGroups.push(g)
    else other.push(g)
  }

  const buckets = new Map<string, ContractVisitGroup[]>()
  for (const g of dsnyGroups) {
    const sig = visitGroupAssignmentSignature(g)
    const list = buckets.get(sig) || []
    list.push(g)
    buckets.set(sig, list)
  }

  const mergedDsny: ContractVisitGroup[] = []
  Array.from(buckets.values()).forEach((list) => {
    if (list.length === 1) {
      mergedDsny.push(list[0])
      return
    }
    const days = Array.from(
      new Set(list.flatMap((g: ContractVisitGroup) => g.recurrence_days || []))
    ).sort((a, b) => a - b)
    const base = list[0]
    const dayLabel =
      days.length > 0 ? days.map((d) => DAY_LABELS_SHORT[d]).join('/') : 'Unscheduled'
    const titleMatch = base.name?.match(/^(.+?)\s*\([^)]*\)\s*$/)
    const groupTitle = titleMatch?.[1]?.trim() || base.name || 'DSNY Exterior'
    mergedDsny.push(
      syncGroupServiceIds({
        ...base,
        name: `${groupTitle} (${dayLabel})`,
        recurrence_days: days,
      })
    )
  })

  return [...other, ...mergedDsny]
}

export function lookupVisitBoardService(
  serviceId: string,
  boardServices: DsnyVisitBoardService[]
): DsnyVisitBoardService | undefined {
  const direct = boardServices.find((s) => s.id === serviceId)
  if (direct) return direct
  if (serviceId.startsWith('dsny_')) {
    const rowKey = serviceId.slice('dsny_'.length)
    // Board chips may use persisted child uuids while assignments still carry dsny_* temps.
    const byName = boardServices.find((s) => {
      const name = String(s.service_name || s.name || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '_')
      return name === rowKey || name === rowKey.replace(/_/g, ' ')
    })
    if (byName) return byName
    return boardServices.find((s) => s.id === serviceId)
  }
  const withPrefix = `dsny_${serviceId}`
  return boardServices.find((s) => s.id === withPrefix)
}

/** Merge matching DSNY day groups and align assignments to per-row board ids. */
export function finalizeDsnyVisitGroupsAfterSeed(
  groups: ContractVisitGroup[],
  packageBoardId: string,
  boardServices: DsnyVisitBoardService[],
  scheduleRows: DsnyScheduleRowFull[],
  contractServices: any[] = []
): ContractVisitGroup[] {
  let next = expandPackageAssignmentsToScheduleRows(
    groups,
    scheduleRows,
    packageBoardId,
    contractServices
  )
  next = consolidateDsnyPackageVisitGroups(next, packageBoardId)
  next = reconcileVisitGroupAssignments(next, boardServices, contractServices)
  next = repairEmptyDsnyVisitGroups(next, scheduleRows)
  next = repairEmptyStandaloneVisitGroups(next, scheduleRows)
  next = assignMissingWeeklyBoardServices(next, boardServices, scheduleRows)
  next = attachMissingNonWeeklyBoardServices(next, boardServices)
  if (hasMisplacedPiggybackServices(next, boardServices)) {
    next = healMisplacedPiggybackAssignments(next, boardServices)
  }
  return next
}

/** Expand persisted package uuids into per-row board ids after loading from the DB. */
export function reconcileLoadedDsnyVisitGroups(
  groups: ContractVisitGroup[],
  contractServices: any[],
  options: { packageLabel?: string; scheduleRows?: DsnyScheduleRowFull[] } = {}
): ContractVisitGroup[] {
  if (groups.length === 0 || !contractServices.some((s) => isDsnyPackageService(s))) {
    return groups
  }

  const mappings = groups.flatMap((g) =>
    getGroupAssignments(g).map((a) => ({
      contract_service_id: a.service_id,
      schedule_mode: a.schedule_mode,
      recurrence_type: a.recurrence_type,
      recurrence_weekday: a.recurrence_weekday,
      recurrence_occurrence: a.recurrence_occurrence,
      recurrence_day_of_month: a.recurrence_day_of_month,
      recurrence_nearest_visit: a.recurrence_nearest_visit,
      visit_group_name: g.name,
      visit_group_dsny_integration: !!g.dsny_integration,
    }))
  )

  const rows =
    options.scheduleRows ??
    hydrateDsnyScheduleRowsFromContractServices(contractServices, mappings)
  const packageLabel =
    options.packageLabel ||
    contractServices.find((s) => isDsnyPackageService(s))?.service_name ||
    'Building Upkeep'
  const board = buildDsnyVisitBoardServices(rows, contractServices, packageLabel)

  return finalizeDsnyVisitGroupsAfterSeed(
    groups,
    findDsnyPackageBoardId(contractServices),
    board,
    rows,
    contractServices
  )
}

function boardServiceNeedsPiggybackHost(s: DsnyVisitBoardService): boolean {
  if (s.snap_to_closest) return true
  return isPiggybackCombineRow({
    type: 'custom',
    combine_target: s.combine_target,
    combine_with_dsny: s.combine_with_dsny,
    recurrence_type: (s.recurrence_type as DsnyRowRecurrenceType) || 'weekly',
    snap_to_closest_visit: s.snap_to_closest,
  })
}

function isCorrectPiggybackHost(
  group: ContractVisitGroup,
  combineTarget: DsnyCombineTarget
): boolean {
  if (combineTarget === 'hallway') return /^Hallway Cleaning\b/i.test(group.name || '')
  if (combineTarget === 'dsny') return !!group.dsny_integration
  return false
}

/**
 * True when a hallway/DSNY-combine non-weekly board service is missing, is primary on
 * its own orphan group, or is piggybacked onto the wrong host.
 */
export function hasMisplacedPiggybackServices(
  groups: ContractVisitGroup[],
  board: DsnyVisitBoardService[]
): boolean {
  for (const s of board) {
    if (!boardServiceNeedsPiggybackHost(s)) continue
    const combineTarget = s.combine_target
    if (combineTarget !== 'hallway' && combineTarget !== 'dsny') continue

    let found: { group: ContractVisitGroup; mode: string } | null = null
    for (const g of groups) {
      const a = getGroupAssignments(g).find((x) => x.service_id === s.id)
      if (a) {
        found = { group: g, mode: a.schedule_mode || 'primary' }
        break
      }
    }
    if (!found) return true
    if (found.mode === 'piggyback' && isCorrectPiggybackHost(found.group, combineTarget)) {
      continue
    }
    return true
  }
  return false
}

/**
 * Move hallway/DSNY-combine non-weekly services off orphan/wrong hosts and onto
 * the correct weekly piggyback host. Preserves existing weekly group structure.
 */
export function healMisplacedPiggybackAssignments(
  groups: ContractVisitGroup[],
  board: DsnyVisitBoardService[]
): ContractVisitGroup[] {
  if (groups.length === 0 || !hasMisplacedPiggybackServices(groups, board)) return groups

  const piggybackIds = new Set(
    board.filter((s) => boardServiceNeedsPiggybackHost(s)).map((s) => s.id)
  )

  let next = groups
    .map((g) => {
      const assignments = getGroupAssignments(g).filter((a) => {
        if (!piggybackIds.has(a.service_id)) return true
        const svc = board.find((s) => s.id === a.service_id)
        if (!svc) return true
        const ct = svc.combine_target
        if (ct !== 'hallway' && ct !== 'dsny') return true
        return a.schedule_mode === 'piggyback' && isCorrectPiggybackHost(g, ct)
      })
      return syncGroupServiceIds({ ...g, service_assignments: assignments })
    })
    .filter((g) => getGroupAssignments(g).length > 0)

  next = attachMissingNonWeeklyBoardServices(next, board)
  return next
}

/**
 * Attach non-weekly board services missing from visit groups.
 * Piggyback-combine / snap rows join the hallway or DSNY weekly host; true
 * standalones get their own suggested groups. Never suggest a piggyback row in
 * isolation (that creates "Drains (Monthly)" with no days).
 */
export function attachMissingNonWeeklyBoardServices(
  groups: ContractVisitGroup[],
  boardServices: ServiceForGrouping[],
  options: {
    contractId?: string
    contractStartDate?: string | null
    contractEndDate?: string | null
  } = {}
): ContractVisitGroup[] {
  if (groups.length === 0 || boardServices.length === 0) return groups

  const assigned = new Set(
    groups.flatMap((g) => getGroupAssignments(g).map((a) => a.service_id))
  )
  const missing = boardServices.filter((s) => !assigned.has(s.id))
  if (missing.length === 0) return groups

  const missingPiggyback = missing.filter((s) =>
    boardServiceNeedsPiggybackHost(s as DsnyVisitBoardService)
  )
  const missingStandalone = missing.filter(
    (s) =>
      !!s.recurrence_type &&
      s.recurrence_type !== 'weekly' &&
      !missingPiggyback.some((p) => p.id === s.id)
  )
  if (missingPiggyback.length === 0 && missingStandalone.length === 0) return groups

  let next = [...groups]

  for (const s of missingPiggyback) {
    const wantsHallway = s.combine_target === 'hallway'
    const wantsDsny =
      s.combine_target === 'dsny' ||
      (!wantsHallway && (!!s.combine_with_dsny || !!s.dsny_integration))
    const target =
      (wantsHallway
        ? next.find((g) => /^Hallway Cleaning\b/i.test(g.name || ''))
        : wantsDsny
          ? next.find((g) => g.dsny_integration)
          : undefined) ||
      next.find((g) => g.dsny_integration) ||
      next.find((g) => /^Hallway Cleaning\b/i.test(g.name || '')) ||
      next.find((g) => g.recurrence_type === 'weekly')

    const rtype = s.recurrence_type || 'monthly'
    if (target) {
      const assignment = {
        service_id: s.id,
        schedule_mode: 'piggyback' as const,
        recurrence_type: rtype,
        recurrence_day_of_month: s.recurrence_day_of_month ?? null,
        recurrence_weekday: s.recurrence_weekday ?? null,
        recurrence_occurrence: s.recurrence_occurrence ?? null,
        recurrence_nearest_visit: !!s.snap_to_closest,
      }
      next = next.map((g) =>
        g.id === target.id
          ? syncGroupServiceIds({
              ...g,
              assigned_crew_id: s.assigned_crew_id || g.assigned_crew_id || null,
              assigned_employee_id:
                s.assigned_employee_id || g.assigned_employee_id || null,
              service_assignments: [...getGroupAssignments(g), assignment],
            })
          : g
      )
    } else {
      next = [
        ...next,
        ...suggestVisitGroupsDayCentric(
          [{ ...s, service_type_id: `standalone_${s.id}` }],
          [],
          options.contractId || '',
          options.contractStartDate,
          options.contractEndDate
        ),
      ]
    }
  }

  if (missingStandalone.length > 0) {
    next = [
      ...next,
      ...suggestVisitGroupsDayCentric(
        missingStandalone.map((s) => ({
          ...s,
          service_type_id: s.service_type_id || `standalone_${s.id}`,
        })),
        [],
        options.contractId || '',
        options.contractStartDate,
        options.contractEndDate
      ),
    ]
  }

  return next
}

/**
 * Load-time heal for View/Edit/Activate: reconcile saved groups, then re-derive from
 * the schedule board when groups are missing, leave board services unassigned, or
 * leave hallway/DSNY piggybacks on their own "No days set" orphan groups.
 */
export function ensureDsnyVisitGroupsForDisplay(
  groups: ContractVisitGroup[],
  contractServices: any[],
  options: {
    scheduleRows?: DsnyScheduleRowFull[]
    packageLabel?: string
    contractId?: string
    contractStartDate?: string | null
    contractEndDate?: string | null
  } = {}
): ContractVisitGroup[] {
  if (!contractServices.some((s) => isDsnyPackageService(s))) return groups

  const mappings = groups.flatMap((g) =>
    getGroupAssignments(g).map((a) => ({
      contract_service_id: a.service_id,
      schedule_mode: a.schedule_mode,
      recurrence_type: a.recurrence_type,
      recurrence_weekday: a.recurrence_weekday,
      recurrence_occurrence: a.recurrence_occurrence,
      recurrence_day_of_month: a.recurrence_day_of_month,
      recurrence_nearest_visit: a.recurrence_nearest_visit,
      visit_group_name: g.name,
      visit_group_dsny_integration: !!g.dsny_integration,
    }))
  )
  const rows =
    options.scheduleRows ??
    hydrateDsnyScheduleRowsFromContractServices(contractServices, mappings)
  const packageLabel =
    options.packageLabel ||
    contractServices.find((s) => isDsnyPackageService(s))?.service_name ||
    'Building Upkeep'
  const board = buildDsnyVisitBoardServices(rows, contractServices, packageLabel)
  if (board.length === 0) return groups

  let next =
    groups.length > 0
      ? reconcileLoadedDsnyVisitGroups(groups, contractServices, {
          scheduleRows: rows,
          packageLabel,
        })
      : []

  const finish = (result: ContractVisitGroup[]) =>
    preserveVisitGroupTargetAssignees(
      groups,
      syncVisitGroupAssigneesFromScheduleRows(result, rows)
    )

  const unassigned = getUnassignedServiceIds(
    next,
    board.map((s) => s.id)
  )
  const misplaced = hasMisplacedPiggybackServices(next, board)
  if (next.length > 0 && unassigned.length === 0 && !misplaced) {
    return finish(next)
  }

  // Weekly hosts already exist but drains/etc. sit on orphan groups → relocate only.
  if (next.length > 0 && misplaced && unassigned.length === 0) {
    return finish(healMisplacedPiggybackAssignments(next, board))
  }

  const seedServices: ServiceForGrouping[] = board.map((s) => ({
    ...s,
    service_type_id:
      s.combine_target === 'dsny'
        ? 'maintenance_dsny'
        : s.combine_target === 'hallway'
          ? 'hallway_cleaning'
          : `standalone_${s.id}`,
  }))
  const suggested = finalizeDsnyVisitGroupsAfterSeed(
    suggestVisitGroupsDayCentric(
      seedServices,
      [],
      options.contractId || '',
      options.contractStartDate,
      options.contractEndDate
    ),
    findDsnyPackageBoardId(contractServices),
    board,
    rows,
    contractServices
  )

  // Preserve assignees from any saved groups that share the same target
  // (Exterior vs Hallway). Day signatures can change during re-derive/consolidate,
  // so do not require an exact recurrence_days match.
  const withPreserved = suggested.map((group) => {
    const sameTarget = (g: ContractVisitGroup) =>
      !!g.dsny_integration === !!group.dsny_integration &&
      (!!g.dsny_integration ||
        /^Hallway Cleaning\b/i.test(g.name || '') ===
          /^Hallway Cleaning\b/i.test(group.name || ''))
    const exact = groups.find(
      (g) =>
        sameTarget(g) &&
        JSON.stringify([...(g.recurrence_days || [])].sort()) ===
          JSON.stringify([...(group.recurrence_days || [])].sort())
    )
    const match =
      exact ||
      groups.find(
        (g) => sameTarget(g) && (g.assigned_crew_id || g.assigned_employee_id)
      ) ||
      groups.find(sameTarget)
    if (!match) return group
    return {
      ...group,
      assigned_crew_id: match.assigned_crew_id || group.assigned_crew_id || null,
      assigned_employee_id: match.assigned_employee_id || group.assigned_employee_id || null,
    }
  })
  return finish(withPreserved)
}

/**
 * Copy Exterior (DSNY) / Interior (Hallway) assignees from `source` groups onto
 * `target` when the target slot is blank. Used after ensure/re-derive so a
 * structure rebuild cannot drop DB-loaded assignees.
 */
export function preserveVisitGroupTargetAssignees(
  source: ContractVisitGroup[],
  target: ContractVisitGroup[]
): ContractVisitGroup[] {
  if (source.length === 0 || target.length === 0) return target

  const pick = (pred: (g: ContractVisitGroup) => boolean) =>
    source.find((g) => pred(g) && (g.assigned_crew_id || g.assigned_employee_id)) ||
    source.find(pred)

  const exterior = pick((g) => !!g.dsny_integration)
  const interior = pick((g) => /^Hallway Cleaning\b/i.test(g.name || ''))

  return target.map((group) => {
    if (group.dsny_integration && exterior) {
      if (group.assigned_crew_id || group.assigned_employee_id) return group
      if (!exterior.assigned_crew_id && !exterior.assigned_employee_id) return group
      return {
        ...group,
        assigned_crew_id: exterior.assigned_crew_id || null,
        assigned_employee_id: exterior.assigned_employee_id || null,
      }
    }
    if (/^Hallway Cleaning\b/i.test(group.name || '') && interior) {
      if (group.assigned_crew_id || group.assigned_employee_id) return group
      if (!interior.assigned_crew_id && !interior.assigned_employee_id) return group
      return {
        ...group,
        assigned_crew_id: interior.assigned_crew_id || null,
        assigned_employee_id: interior.assigned_employee_id || null,
      }
    }
    return group
  })
}

/**
 * Copy Exterior (DSNY) / Interior (Hallway) assignees from schedule rows onto
 * matching visit groups before persist. Keeps existing group UUIDs on edit.
 */
export function syncVisitGroupAssigneesFromScheduleRows(
  groups: ContractVisitGroup[],
  rows: Array<{
    type?: string
    combine_target?: DsnyCombineTarget
    combine_with_dsny?: boolean
    assigned_crew_id?: string | null
    assigned_employee_id?: string | null
  }>
): ContractVisitGroup[] {
  if (groups.length === 0 || rows.length === 0) return groups

  // Prefer a row that already has an assignee (not merely the first Exterior/Interior
  // target). Blank leading rows must not mask assignments on later rows.
  const pickTarget = (target: 'dsny' | 'hallway') => {
    const matches = rows.filter((row) => resolveDsnyCombineTarget(row as any) === target)
    return (
      matches.find((row) => row.assigned_crew_id || row.assigned_employee_id) || matches[0]
    )
  }
  const exterior = pickTarget('dsny')
  const interior = pickTarget('hallway')

  return groups.map((group) => {
    if (group.dsny_integration && exterior) {
      // Only push row → group when the schedule UI has an assignee. Leaves
      // VisitScheduleBoard-only overrides intact when rows are still blank.
      if (exterior.assigned_crew_id == null && exterior.assigned_employee_id == null) {
        return group
      }
      return {
        ...group,
        assigned_crew_id: exterior.assigned_crew_id ?? null,
        assigned_employee_id: exterior.assigned_employee_id ?? null,
      }
    }
    if (/^Hallway Cleaning\b/i.test(group.name || '') && interior) {
      if (interior.assigned_crew_id == null && interior.assigned_employee_id == null) {
        return group
      }
      return {
        ...group,
        assigned_crew_id: interior.assigned_crew_id ?? null,
        assigned_employee_id: interior.assigned_employee_id ?? null,
      }
    }
    return group
  })
}

/**
 * Build visit groups from the live DSNY schedule (create/save path).
 * Prefer this over trusting possibly-stale React board state at submit time.
 */
export function buildDsnyVisitGroupsFromSchedule(
  rows: DsnyScheduleRowFull[],
  options: {
    contractId?: string
    contractStartDate?: string | null
    contractEndDate?: string | null
    contractServices?: any[]
    maintenanceCategoryId?: string
    packageLabel?: string
  } = {}
): ContractVisitGroup[] {
  const contractServices = options.contractServices || []
  const board = buildDsnyVisitBoardServices(rows, contractServices, options.packageLabel)
  if (board.length === 0) return []

  const seedServices: ServiceForGrouping[] = board.map((s) => ({
    ...s,
    service_type_id:
      s.combine_target === 'dsny'
        ? options.maintenanceCategoryId || 'maintenance_dsny'
        : s.combine_target === 'hallway'
          ? 'hallway_cleaning'
          : `standalone_${s.id}`,
  }))

  let groups = finalizeDsnyVisitGroupsAfterSeed(
    suggestVisitGroupsDayCentric(
      seedServices,
      [],
      options.contractId || '',
      options.contractStartDate,
      options.contractEndDate
    ),
    findDsnyPackageBoardId(contractServices),
    board,
    rows,
    contractServices
  )

  // Belt-and-suspenders for create/save: finalize does not attach piggybacks that
  // suggest missed (e.g. host created in a later repair). Never leave hallway/DSNY
  // combine rows as standalone "No days set" orphans.
  groups = attachMissingNonWeeklyBoardServices(groups, board, {
    contractId: options.contractId,
    contractStartDate: options.contractStartDate,
    contractEndDate: options.contractEndDate,
  })
  if (hasMisplacedPiggybackServices(groups, board)) {
    groups = healMisplacedPiggybackAssignments(groups, board)
  }
  return syncVisitGroupAssigneesFromScheduleRows(groups, rows)
}

/**
 * Map temp create-time service ids (`dsny_garbage`, …) to persisted UUIDs.
 * Matches by service_name (unique per DSNY child) rather than insert-return order.
 */
export function buildContractServiceIdMap(
  servicesToSave: Array<{ id: string; service_name?: string }>,
  savedServices: Array<{ id: string; service_name?: string | null }>
): Map<string, string> {
  const map = new Map<string, string>()
  const pool = savedServices.map((s) => ({ ...s }))

  for (const temp of servicesToSave) {
    const name = String(temp.service_name || '').trim().toLowerCase()
    const idx = pool.findIndex(
      (s) => String(s.service_name || '').trim().toLowerCase() === name
    )
    if (idx >= 0) {
      map.set(temp.id, pool[idx].id)
      pool.splice(idx, 1)
    }
  }

  // Fallback: any remaining temps by original index when lengths still align
  if (map.size < servicesToSave.length && servicesToSave.length === savedServices.length) {
    servicesToSave.forEach((temp, i) => {
      if (!map.has(temp.id) && savedServices[i]) {
        map.set(temp.id, savedServices[i].id)
      }
    })
  }

  return map
}

/** Count visit-group assignments that cannot resolve through the service id map. */
export function countUnresolvedVisitGroupAssignments(
  groups: ContractVisitGroup[],
  serviceIdMap: Map<string, string>
): number {
  let unresolved = 0
  for (const group of groups) {
    for (const a of getGroupAssignments(group)) {
      if (!resolveServiceIdForPersist(a.service_id, serviceIdMap, true)) unresolved++
    }
  }
  return unresolved
}

/** True when a temp/per-row id represents a weekly pickup row merged into the package. */
export function isMergedWeeklyRowServiceId(serviceId: string): boolean {
  if (!serviceId.startsWith('dsny_')) return false
  const rowId = serviceId.slice('dsny_'.length)
  return COMBINED_WEEKLY_ROW_TYPES.has(rowId)
}

/**
 * Align visit-group assignment ids with the per-row board services. Expands
 * persisted Building Upkeep ids back into schedule-row ids when needed.
 */
export function reconcileVisitGroupAssignments(
  groups: ContractVisitGroup[],
  boardServices: DsnyVisitBoardService[],
  contractServices: any[] = []
): ContractVisitGroup[] {
  const boardIds = new Set(boardServices.map((s) => s.id))
  const packageBoardId = findDsnyPackageBoardId(contractServices)

  const remapId = (serviceId: string): string => {
    if (boardIds.has(serviceId)) return serviceId
    const resolved = lookupVisitBoardService(serviceId, boardServices)
    if (resolved) return resolved.id
    return serviceId
  }

  return groups.map((group) => {
    const seen = new Set<string>()
    const assignments = getGroupAssignments(group)
      .map((a) => ({ ...a, service_id: remapId(a.service_id) }))
      .filter((a) => {
        if (isPackageAssignmentId(a.service_id, packageBoardId, contractServices)) return false
        if (!boardIds.has(a.service_id)) return false
        if (seen.has(a.service_id)) return false
        seen.add(a.service_id)
        return true
      })

    return syncGroupServiceIds({ ...group, service_assignments: assignments })
  })
}

/** Fill empty DSNY day-slot groups with the pickup rows scheduled on those days. */
export function repairEmptyDsnyVisitGroups(
  groups: ContractVisitGroup[],
  rows: DsnyScheduleRowFull[]
): ContractVisitGroup[] {
  return groups.map((group) => {
    if (!group.dsny_integration) return group
    if (getGroupAssignments(group).length > 0) return group

    const groupDays = new Set(group.recurrence_days || [])
    const rowAssignments: Array<{ service_id: string; schedule_mode: 'primary' }> = []
    for (const row of rows) {
      if (resolveDsnyCombineTarget(row) !== 'dsny') continue
      if (!(row.days || []).some((d) => groupDays.has(d))) continue
      rowAssignments.push({
        service_id: row.persistedServiceId || `dsny_${row.id}`,
        schedule_mode: 'primary',
      })
    }
    if (rowAssignments.length === 0) return group
    return syncGroupServiceIds({ ...group, service_assignments: rowAssignments })
  })
}

/** Place weekly board services still unassigned into matching visit groups. */
export function assignMissingWeeklyBoardServices(
  groups: ContractVisitGroup[],
  boardServices: DsnyVisitBoardService[],
  rows: DsnyScheduleRowFull[]
): ContractVisitGroup[] {
  const assigned = new Set(
    groups.flatMap((g) => getGroupAssignments(g).map((a) => a.service_id))
  )
  let next = groups.map((g) => ({ ...g }))

  for (const svc of boardServices) {
    if (assigned.has(svc.id)) continue
    const rtype = svc.recurrence_type || 'weekly'
    if (rtype !== 'weekly') continue

    const svcDays = svc.recurrence_days || []
    if (svcDays.length === 0) continue

    const isDsnyChip = !!(svc.dsny_integration || svc.combine_target === 'dsny')

    const targetIdx = next.findIndex((g) => {
      const groupDays = g.recurrence_days || []
      if (!svcDays.some((d) => groupDays.includes(d))) return false
      if (isDsnyChip) return !!g.dsny_integration
      if (svc.combine_target === 'hallway') {
        return !g.dsny_integration && /^Hallway Cleaning\b/i.test(g.name || '')
      }
      return !g.dsny_integration
    })

    if (targetIdx < 0) continue

    const target = next[targetIdx]
    next[targetIdx] = syncGroupServiceIds({
      ...target,
      service_assignments: [
        ...getGroupAssignments(target),
        { service_id: svc.id, schedule_mode: 'primary' },
      ],
    })
    assigned.add(svc.id)
  }

  return next
}

/** Fill empty non-DSNY groups (e.g. Hallway Cleaning) from matching weekly schedule rows. */
export function repairEmptyStandaloneVisitGroups(
  groups: ContractVisitGroup[],
  rows: DsnyScheduleRowFull[]
): ContractVisitGroup[] {
  return groups.map((group) => {
    if (group.dsny_integration) return group
    if (getGroupAssignments(group).length > 0) return group

    const groupDays = new Set(group.recurrence_days || [])
    const isHallwayGroup = /^Hallway Cleaning\b/i.test(group.name || '')
    const rowAssignments: Array<{ service_id: string; schedule_mode: 'primary' }> = []
    for (const row of rows) {
      if (!rowQualifiesForBoard(row) || !isRowWeekly(row)) continue
      const combineTarget = resolveDsnyCombineTarget(row)
      if (combineTarget === 'dsny') continue
      if (combineTarget === 'hallway' && !isHallwayGroup) continue
      if (
        combineTarget === null &&
        (isHallwayGroup ||
          !String(group.name || '').toLowerCase().startsWith(String(row.name || '').toLowerCase()))
      ) {
        continue
      }
      if (!(row.days || []).some((d) => groupDays.has(d))) continue
      rowAssignments.push({
        service_id: row.persistedServiceId || `dsny_${row.id}`,
        schedule_mode: 'primary',
      })
    }
    if (rowAssignments.length === 0) return group
    return syncGroupServiceIds({ ...group, service_assignments: rowAssignments })
  })
}

/** Map schedule row types → catalogue child items for DSNY package children. */
export function buildDsnyChildItemsByType(
  serviceItemsGrouped: Array<{ items?: Array<{ id: string; name: string }> }>
): Partial<Record<string, { id: string; name: string }>> {
  const byName = new Map<string, { id: string; name: string }>()
  for (const group of serviceItemsGrouped) {
    for (const item of group.items || []) {
      byName.set(String(item.name || '').trim().toLowerCase(), { id: item.id, name: item.name })
    }
  }
  const pick = (name: string) => byName.get(name.toLowerCase())
  return {
    garbage: pick('Garbage'),
    recycling: pick('Recycling'),
    organics: pick('Organics'),
    bulk: pick('Bulk'),
    hallway_cleaning: pick('Hallway Cleaning'),
  }
}

export interface DeriveDsnyArtifactsOptions {
  /** Catalogue package item (Building Upkeep or Exterior Maintenance). */
  buildingUpkeepItem?: { id: string; name: string; base_price: number } | null
  packageKind?: DsnyPackageKind
  maintenanceDSNYCategoryId?: string
  proposalLineItemId?: string | null
  /** Price for the DSNY package. Order of precedence is up to the caller
   *  (state amount → proposal amount → catalog base price). */
  amount?: number | null
  /** Create the package service even without weekly days (e.g. when a
   *  proposal line item or existing line item must stay linked to a service). */
  forcePackage?: boolean
  /** Catalogue child items keyed by schedule row type (garbage, recycling, …). */
  childItemsByType?: Partial<
    Record<string, { id: string; name: string } | null | undefined>
  >
}

export interface DsnyContractArtifacts {
  /** Building Upkeep billing package + $0 child services + standalone non-weekly. */
  services: ContractServiceForLink[]
  /** Columns to persist on the contracts row so displays/reports match. */
  schedulePatch: {
    garbage_schedule: number[]
    recycling_schedule: number[]
    organics_schedule: number[]
    bulk_schedule: number[]
    interior_cleaning_schedule: number[]
    master_weekly_schedule: number[]
  }
}

/**
 * Derive schedule-driven artifacts from DSNY rows.
 * Building Upkeep is the billing package; weekly collection types become real
 * $0 child contract services (generic package shape). Non-weekly custom/hallway
 * rows remain standalone services.
 */
export function deriveDsnyContractArtifacts(
  rows: DsnyScheduleRowFull[],
  options: DeriveDsnyArtifactsOptions
): DsnyContractArtifacts {
  const packageKind: DsnyPackageKind =
    options.packageKind ||
    detectDsnyPackageKind(options.buildingUpkeepItem?.name || 'Building Upkeep')
  const omitHallway = packageKind === 'exterior_maintenance'

  const weeklyRows = rows.filter(
    (row) =>
      isRowWeekly(row) &&
      !(omitHallway && row.type === 'hallway_cleaning') &&
      (row.type !== 'custom' || customRowHasIdentity(row))
  )
  const nonWeeklyRows = rows.filter(
    (row) =>
      (row.type === 'custom' || (!omitHallway && row.type === 'hallway_cleaning')) &&
      !isRowWeekly(row) &&
      (row.type !== 'custom' || customRowHasIdentity(row))
  )

  const rowDays = (type: string) =>
    weeklyRows.find((r) => r.type === type)?.days || []
  const garbage = rowDays('garbage')
  const recycling = rowDays('recycling')
  const organics = rowDays('organics')
  const bulk = rowDays('bulk')
  const hallway = omitHallway ? [] : rowDays('hallway_cleaning')
  const allWeeklyDays = Array.from(new Set(weeklyRows.flatMap((r) => r.days || []))).sort(
    (a, b) => a - b
  )
  const defaultPackageName =
    packageKind === 'exterior_maintenance' ? 'Exterior Maintenance' : 'Building Upkeep'

  const services: ContractServiceForLink[] = []

  if (allWeeklyDays.length > 0 || options.forcePackage) {
    services.push({
      id: DSNY_PACKAGE_SERVICE_ID,
      service_item_id: options.buildingUpkeepItem?.id || '',
      service_type: options.maintenanceDSNYCategoryId || '',
      service_name: options.buildingUpkeepItem?.name || defaultPackageName,
      amount: options.amount ?? options.buildingUpkeepItem?.base_price ?? 0,
      description: options.buildingUpkeepItem?.name || defaultPackageName,
      scheduled_date: null,
      scheduled_time: null,
      is_maintenance: true,
      proposal_line_item_id: options.proposalLineItemId || undefined,
      schedule: {
        recurrence_type: 'weekly',
        recurrence_days: allWeeklyDays,
        dsny_integration: true,
        garbage_schedule: garbage,
        recycling_schedule: recycling,
        organics_schedule: organics,
        bulk_schedule: bulk,
        interior_cleaning_schedule: hallway,
      },
    })
  }

  // Real $0 child services — visit board / generator use these, not the package alone.
  for (const row of weeklyRows) {
    if (omitHallway && row.type === 'hallway_cleaning') continue
    const combineTarget = resolveDsnyCombineTarget(row)
    const serviceName = resolveRowServiceName(row, options.childItemsByType)
    services.push({
      id: row.persistedServiceId ?? `dsny_${row.id}`,
      service_item_id: resolveRowServiceItemId(row, options.childItemsByType),
      service_type: options.maintenanceDSNYCategoryId || '',
      service_name: serviceName,
      amount: 0,
      description: serviceName,
      scheduled_date: null,
      scheduled_time: null,
      is_maintenance: true,
      schedule: {
        recurrence_type: 'weekly',
        recurrence_days: row.days || [],
        dsny_integration: combineTarget === 'dsny',
        combine_target: combineTarget ?? null,
        garbage_schedule: row.type === 'garbage' ? row.days || [] : [],
        recycling_schedule: row.type === 'recycling' ? row.days || [] : [],
        organics_schedule: row.type === 'organics' ? row.days || [] : [],
        bulk_schedule: row.type === 'bulk' ? row.days || [] : [],
        interior_cleaning_schedule:
          row.type === 'hallway_cleaning' ? row.days || [] : [],
      },
    })
  }

  for (const row of nonWeeklyRows) {
    const rec = buildRowRecurrenceFields(row)
    const serviceName = resolveRowServiceName(row, options.childItemsByType)
    services.push({
      id: row.persistedServiceId ?? `dsny_${row.id}`,
      service_item_id: resolveRowServiceItemId(row, options.childItemsByType),
      service_type: '',
      service_name: serviceName,
      amount: 0,
      description: row.name || serviceName || 'Maintenance',
      scheduled_date: null,
      scheduled_time: null,
      is_maintenance: true,
      schedule: {
        recurrence_type: rec.recurrence_type,
        // Monthly/daily cadence is carried on the visit group / piggyback mapping,
        // not as weekday recurrence on the service itself
        recurrence_days:
          rec.recurrence_type === 'monthly' || rec.recurrence_type === 'daily'
            ? []
            : row.days || [],
        dsny_integration: false,
        // Persist visit target so hydrate can restore hallway/DSNY piggyback intent
        // even if the service was incorrectly saved on its own orphan group.
        combine_target: resolveDsnyCombineTarget(row) ?? null,
        garbage_schedule: [],
        recycling_schedule: [],
        organics_schedule: [],
        bulk_schedule: [],
        interior_cleaning_schedule: row.days || [],
        recurrence_weekday: rec.recurrence_weekday,
        recurrence_occurrence: rec.recurrence_occurrence,
        recurrence_day_of_month: rec.recurrence_day_of_month,
      },
    })
  }

  return {
    services,
    schedulePatch: {
      garbage_schedule: garbage,
      recycling_schedule: recycling,
      organics_schedule: organics,
      bulk_schedule: bulk,
      interior_cleaning_schedule: hallway,
      master_weekly_schedule: allWeeklyDays,
    },
  }
}

// ---------------------------------------------------------------------------
// Visit group persistence (shared by Create and Edit)
// ---------------------------------------------------------------------------

/**
 * Resolve a visit-group assignment's service id to a persisted
 * contract_services uuid. Temp ids (dsny_*, service_*, temp-*) must resolve
 * through the map; already-persisted uuids pass through untouched.
 */
export function resolveServiceIdForPersist(
  serviceId: string,
  serviceIdMap?: Map<string, string>,
  strict = false
): string | null {
  if (serviceIdMap) {
    const mapped = resolveContractServiceIdForSave(serviceId, serviceIdMap)
    if (mapped) return mapped
  }
  // Strict mode: only ids that resolved through the map are valid (used right
  // after a delete+reinsert of contract_services, when every old uuid is stale)
  if (strict) return null
  const isTempId =
    serviceId.startsWith('dsny_') ||
    serviceId.startsWith('service_') ||
    serviceId.startsWith('temp-') ||
    serviceId.startsWith('linked_') ||
    serviceId === DSNY_PACKAGE_SERVICE_ID
  return isTempId ? null : serviceId
}

function groupInsertRow(contractId: string, group: ContractVisitGroup) {
  return {
    contract_id: contractId,
    name: group.name,
    recurrence_type: group.recurrence_type || 'weekly',
    recurrence_days: group.recurrence_days || [],
    recurrence_weekday: group.recurrence_weekday ?? null,
    recurrence_occurrence: group.recurrence_occurrence ?? null,
    recurrence_day_of_month: group.recurrence_day_of_month ?? null,
    start_date: group.start_date || null,
    end_date: group.end_date || null,
    scheduled_start_time: group.scheduled_start_time || null,
    scheduled_end_time: group.scheduled_end_time || null,
    billing_driver: group.billing_driver || null,
    dsny_integration: group.dsny_integration || false,
    dsny_pickup_days: group.dsny_pickup_days || [],
    is_active: group.is_active !== false,
    checklist_items: Array.isArray(group.checklist_items) ? group.checklist_items : [],
    checklist_overrides:
      group.checklist_overrides && typeof group.checklist_overrides === 'object'
        ? group.checklist_overrides
        : {},
    assigned_crew_id: group.assigned_crew_id || null,
    assigned_employee_id: group.assigned_employee_id || null,
  }
}

function dedupeMappingsByServiceId<T extends { contract_service_id: string }>(rows: T[]): T[] {
  // Weekly DSNY rows (garbage, bulk, …) all resolve to one Building Upkeep service;
  // the table allows only one row per (visit_group_id, contract_service_id).
  const seen = new Set<string>()
  const out: T[] = []
  for (const row of rows) {
    if (seen.has(row.contract_service_id)) continue
    seen.add(row.contract_service_id)
    out.push(row)
  }
  return out
}

function mappingRows(
  visitGroupId: string,
  group: ContractVisitGroup,
  serviceIdMap?: Map<string, string>,
  strict = false
) {
  const rows = getGroupAssignments(group)
    .map((a) => {
      const contractServiceId = resolveServiceIdForPersist(a.service_id, serviceIdMap, strict)
      if (!contractServiceId) return null
      return {
        visit_group_id: visitGroupId,
        contract_service_id: contractServiceId,
        schedule_mode: a.schedule_mode || 'primary',
        recurrence_type: a.recurrence_type || null,
        recurrence_days: Array.isArray(a.recurrence_days) ? a.recurrence_days : [],
        recurrence_weekday: a.recurrence_weekday ?? null,
        recurrence_occurrence: a.recurrence_occurrence ?? null,
        recurrence_day_of_month: a.recurrence_day_of_month ?? null,
        recurrence_nearest_visit: a.recurrence_nearest_visit ?? null,
      }
    })
    .filter((v): v is NonNullable<typeof v> => !!v)
  return dedupeMappingsByServiceId(rows)
}

/** Payload shape consumed by the replace_contract_visit_groups RPC. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function rpcPayload(
  contractId: string,
  groups: ContractVisitGroup[],
  serviceIdMap?: Map<string, string>,
  strict = false
) {
  return groups.map((group) => ({
    // Persisted groups keep their id so the RPC updates them in place (jobs
    // reference the group id via fk_jobs_visit_group). New groups have a temp
    // id → send null so the RPC inserts a fresh row.
    id: UUID_RE.test(String(group.id || '')) ? group.id : null,
    ...groupInsertRow(contractId, group),
    services: mappingRows('', group, serviceIdMap, strict).map(
      ({ visit_group_id: _ignored, ...rest }) => rest
    ),
  }))
}

/**
 * Older replace_contract_visit_groups RPCs ignore assigned_* columns. After a
 * successful RPC, patch crew/employee from the in-memory groups (match by id,
 * else by DSNY/Hallway + days signature).
 */
async function patchVisitGroupAssigneesAfterRpc(
  contractId: string,
  groups: ContractVisitGroup[]
): Promise<void> {
  if (groups.length === 0) return
  const { data: saved, error } = await supabase
    .from('contract_visit_groups')
    .select('id, name, dsny_integration, recurrence_days, assigned_crew_id, assigned_employee_id')
    .eq('contract_id', contractId)
  if (error || !saved?.length) return

  const dayKey = (days?: number[] | null) =>
    JSON.stringify([...(days || [])].sort((a, b) => a - b))

  for (const group of groups) {
    const desiredCrew = group.assigned_crew_id || null
    const desiredEmp = group.assigned_employee_id || null
    if (desiredCrew == null && desiredEmp == null) continue

    let row =
      (UUID_RE.test(String(group.id || ''))
        ? saved.find((s: any) => s.id === group.id)
        : undefined) ||
      saved.find(
        (s: any) =>
          !!s.dsny_integration === !!group.dsny_integration &&
          dayKey(s.recurrence_days) === dayKey(group.recurrence_days) &&
          (!!s.dsny_integration ||
            /^Hallway Cleaning\b/i.test(s.name || '') ===
              /^Hallway Cleaning\b/i.test(group.name || ''))
      )
    if (!row) continue
    if (row.assigned_crew_id === desiredCrew && row.assigned_employee_id === desiredEmp) {
      continue
    }
    await supabase
      .from('contract_visit_groups')
      .update({
        assigned_crew_id: desiredCrew,
        assigned_employee_id: desiredEmp,
      })
      .eq('id', row.id)
      .eq('contract_id', contractId)
  }
}

/**
 * Atomic path: one RPC call replaces a contract's visit groups and mappings
 * in a single transaction. Returns true when the RPC exists and succeeded.
 */
async function tryAtomicReplace(
  contractId: string,
  groups: ContractVisitGroup[],
  serviceIdMap?: Map<string, string>,
  strict = false
): Promise<boolean> {
  const payload = rpcPayload(contractId, groups, serviceIdMap, strict)
  const { error } = await supabase.rpc('replace_contract_visit_groups', {
    p_contract_id: contractId,
    p_groups: payload,
  })
  if (!error) {
    await patchVisitGroupAssigneesAfterRpc(contractId, groups)
    return true
  }
  // 42883 = undefined_function (migration not applied yet) → fall back
  if ((error as any).code === '42883' || /function .* does not exist/i.test(error.message || '')) {
    return false
  }
  throw error
}

/**
 * Insert visit groups + service mappings for a contract (create path — assumes
 * no existing groups). Errors on individual groups are logged, not thrown, so
 * contract creation is never blocked by one bad group.
 */
export async function persistContractVisitGroups(
  contractId: string,
  groups: ContractVisitGroup[],
  serviceIdMap?: Map<string, string>
): Promise<void> {
  if (groups.length === 0) return

  let saved = false
  try {
    if (await tryAtomicReplace(contractId, groups, serviceIdMap)) {
      saved = true
    }
  } catch (err) {
    console.error('Atomic visit-group save failed, falling back to sequential:', err)
  }

  if (!saved) {
    for (const group of groups) {
      const { data: insertedGroup, error: groupErr } = await supabase
        .from('contract_visit_groups')
        .insert(groupInsertRow(contractId, group))
        .select('id')
        .single()

      if (groupErr || !insertedGroup) {
        console.error('Error saving visit group:', groupErr)
        continue
      }

      const rows = mappingRows(insertedGroup.id, group, serviceIdMap)
      if (rows.length > 0) {
        const { error: mapErr } = await supabase.from('contract_visit_group_services').insert(rows)
        if (mapErr) console.error('Error mapping services to visit group:', mapErr)
      }
    }
  }

  // Create-as-Active inserts the contract before visit groups; the activation
  // trigger runs too early. Re-seed jobs now that groups exist (no-op if draft).
  await regenerateJobsAfterVisitGroupSave(contractId)
}

/**
 * Replace a contract's visit groups (edit path): delete existing groups and
 * mappings, then insert the new set. Throws on failure so callers can surface
 * the error to the user.
 *
 * Set `strict` after a delete+reinsert of contract_services: every assignment
 * service id must then resolve through `serviceIdMap` (old uuid → new uuid) so
 * stale ids are dropped instead of failing the insert.
 */
export async function replaceContractVisitGroups(
  contractId: string,
  groups: ContractVisitGroup[],
  serviceIdMap?: Map<string, string>,
  options?: { strict?: boolean; regenerateJobs?: boolean }
): Promise<void> {
  const strict = options?.strict ?? false
  if (await tryAtomicReplace(contractId, groups, serviceIdMap, strict)) {
    if (options?.regenerateJobs !== false) {
      await regenerateJobsAfterVisitGroupSave(contractId)
    }
    return
  }

  // Sequential fallback (non-atomic): delete then insert
  const { data: existing } = await supabase
    .from('contract_visit_groups')
    .select('id')
    .eq('contract_id', contractId)
  const existingIds = (existing || []).map((r: any) => r.id)
  if (existingIds.length > 0) {
    await supabase
      .from('contract_visit_group_services')
      .delete()
      .in('visit_group_id', existingIds)
    await supabase.from('contract_visit_groups').delete().eq('contract_id', contractId)
  }

  for (const group of groups) {
    const { data: inserted, error } = await supabase
      .from('contract_visit_groups')
      .insert(groupInsertRow(contractId, group))
      .select('id')
      .single()
    if (error) throw error
    if (inserted?.id) {
      const rows = mappingRows(inserted.id, group, serviceIdMap, strict)
      if (rows.length > 0) {
        const { error: mapErr } = await supabase
          .from('contract_visit_group_services')
          .insert(rows)
        if (mapErr) throw mapErr
      }
    }
  }

  if (options?.regenerateJobs !== false) {
    await regenerateJobsAfterVisitGroupSave(contractId)
  }
}

/**
 * After visit-group edits on an active recurring contract: clear open auto-
 * generated jobs in the next 90 days (capped by contract end), then rebuild
 * that window from the current visit groups. Completed / in-progress jobs are
 * left alone.
 */
async function regenerateJobsAfterVisitGroupSave(contractId: string): Promise<void> {
  try {
    const { data: contract } = await supabase
      .from('contracts')
      .select('status, start_date, end_date, is_recurring')
      .eq('id', contractId)
      .maybeSingle()
    if (!contract || contract.status !== 'active' || contract.is_recurring === false) return

    const start = new Date()
    start.setHours(0, 0, 0, 0)
    const end = new Date(start)
    end.setDate(end.getDate() + 90)
    if (contract.end_date) {
      const contractEnd = new Date(contract.end_date)
      if (contractEnd < end) end.setTime(contractEnd.getTime())
    }

    const startStr = start.toISOString().split('T')[0]
    const endStr = end.toISOString().split('T')[0]
    const openStatuses = ['scheduled', 'pending', 'assigned', 'unassigned', 'open']

    // Drop open recurring shells in the window so day/service changes fully redo.
    const { error: clearErr } = await supabase
      .from('jobs')
      .delete()
      .eq('contract_id', contractId)
      .eq('is_recurring', true)
      .gte('scheduled_date', startStr)
      .lte('scheduled_date', endStr)
      .in('status', openStatuses)

    if (clearErr) {
      console.warn('Could not clear open jobs before regen:', clearErr)
    }

    await supabase.rpc('generate_jobs_for_contract_range', {
      contract_uuid: contractId,
      start_date_param: startStr,
      end_date_param: endStr,
      dry_run_param: false,
    })

    // Overwrite assignees on future open jobs (visit-group / contract defaults).
    await supabase.rpc('reapply_assignment_defaults_for_contract', {
      p_contract_id: contractId,
    })
  } catch (err) {
    // Visit groups already saved — don't fail the save if regen is unavailable.
    console.warn('Visit groups saved but job refresh failed:', err)
  }
}
